import { ReactNode, useEffect, useMemo, useState } from "react";

import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { chartsAxisHighlightClasses } from "@mui/x-charts/ChartsAxisHighlight";
import { chartsGridClasses } from "@mui/x-charts/ChartsGrid";
import { lineClasses } from "@mui/x-charts/LineChart";
import { getUnixTs, TIME } from "@bridgesplit/utils";
import { referenceLineClasses } from "@mui/x-charts/ChartsReferenceLine";
import { timeDay, timeHour } from "@mui/x-charts-vendor/d3-time";
import { useLocalStorage } from "@bridgesplit/react";

import { AXIS_FORMATTERS, SERIES_FORMATTERS } from "./format";
import {
    SeriesConfig,
    FormatType,
    TimeseriesChartProps,
    XAxisOverride,
    TooltipContext,
    TimeDomainPeriod
} from "./types";
import { FONT_SIZES, useAppPalette } from "../../theme";
import { LineWithProjection, AreaWithProjection } from "./ChartsProjection";
import { getConditionalLabel, getLabelMark } from "./ChartsLabel";
import {
    DEFAULT_CHART_HEIGHT,
    DEFAULT_CHART_MARGIN,
    CHART_AREA_FILL_OPACITY,
    DEFAULT_EXTREMA,
    DEFAULT_TIME_DOMAIN_PERIODS,
    TIME_TICK_STEPS,
    MIN_TIME_TICKS,
    MAX_TIME_TICKS
} from "./constants";

type TooltipRenderer = ((ctx: TooltipContext<unknown>) => ReactNode) | undefined;

export function useChartProps<T extends { date: number }>({
    chartId,
    data,
    height = DEFAULT_CHART_HEIGHT,
    margin = DEFAULT_CHART_MARGIN,
    loading,
    ...props
}: TimeseriesChartProps<T>) {
    const { linePlotSx, areaPlotSx, chartSx } = useChartsSx();
    const {
        hideLegend,
        enableProjection,
        configOptions,
        defaultConfig,
        xAxis,
        xDomain,
        renderTooltip,
        period,
        periodOptions,
        setPeriod,
        headerComponents
    } = props;
    const isTimeAxis = (xAxis?.scaleOverride ?? "time") === "time";

    const [config, setConfig] = useState(defaultConfig ?? 0);

    const { series, axisFormat, references, disableAxisConstraint } = props.series
        ? props
        : props.configOptions[config];
    const dataset: T[] = useMemo(() => (loading ? [] : getValidDataset({ data, series })), [data, series, loading]);

    const seriesConfig = useMemo(
        () =>
            loading ? [] : series.map((seriesConfig, index) => getSeriesConfig({ seriesConfig, axisFormat, index })),
        [series, axisFormat, loading]
    );

    const xExtrema = useMemo(() => getDatasetExtrema({ data: dataset, series }).xExtrema, [dataset, series]);

    const xDomainLimit = useMemo(() => {
        // Caller-controlled override (driven by `useTimeWindow`) sets the window, but the
        // domain never extends earlier than the first data point — clamp the min up to the
        // first event so we don't render empty space before any data exists.
        if (xDomain) {
            const min = typeof xExtrema?.min === "number" ? Math.max(xDomain[0], xExtrema.min) : xDomain[0];
            return { min, max: xDomain[1] };
        }
        if (!xExtrema) return undefined;
        return { min: xExtrema.min, max: xExtrema.max };
    }, [xExtrema, xDomain]);

    const yExtrema = useMemo(() => {
        const xRange =
            xDomainLimit && typeof xDomainLimit.min === "number" && typeof xDomainLimit.max === "number"
                ? { min: xDomainLimit.min, max: xDomainLimit.max }
                : undefined;
        return getDatasetExtrema({ data: dataset, series, xRange }).yExtrema;
    }, [dataset, series, xDomainLimit]);

    // Materialize evenly-spaced time-axis ticks for the visible domain
    const timeTickValues = useMemo<number[] | undefined>(() => {
        if (!isTimeAxis || !xDomainLimit) return undefined;
        const min = typeof xDomainLimit.min === "number" ? xDomainLimit.min : undefined;
        const max = typeof xDomainLimit.max === "number" ? xDomainLimit.max : undefined;
        if (min === undefined || max === undefined || min >= max) return undefined;
        return getTimeAxisTicks(min, max);
    }, [isTimeAxis, xDomainLimit]);

    useEffect(() => {
        setConfig(props.defaultConfig ?? 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartId]);

    const noData = dataset.length === 0;
    return {
        chartsDataProviderProps: {
            margin: { ...margin },
            height,
            xAxis: [{ ...getXAxisOverride(xAxis, timeTickValues), ...xDomainLimit }],
            yAxis: [{ ...getYAxisConfig({ axisFormat, extrema: yExtrema, disableConstraint: disableAxisConstraint }) }],
            dataset,
            series: seriesConfig,
            disableAxisListener: loading
        },
        chartsHeaderProps: {
            loading,
            hide: !loading && noData,
            hideLegend,
            config,
            setConfig,
            configOptions,
            period,
            setPeriod,
            periodOptions,
            components: headerComponents
        },
        chartsSurfaceProps: { sx: chartSx },
        chartsOverlayProps: {
            loading,
            noData
        },
        chartsProjectionProps: { enableProjection },
        linePlotProps: { sx: linePlotSx, slots: { line: LineWithProjection } },
        areaPlotProps: { sx: areaPlotSx, slots: { area: AreaWithProjection } },
        deltaAreaPlotProps: { series },
        // xAxisProps: { disableTicks: true, disableLine: true, offset: references && references.length > 0 ? 16 : 0 },
        xAxisProps: { disableTicks: true, disableLine: true },

        yAxisProps: { disableTicks: true, disableLine: true },
        gridProps: { horizontal: true },
        axisHighlightProps: { x: "line" as const },
        chartsReferencesProps: { references },
        chartsTooltipProps: { enableProjection, isTimeAxis, dataset, renderTooltip: renderTooltip as TooltipRenderer }
    };
}

export const useChartsSx = () => {
    const { divider, textSecondary } = useAppPalette();
    const linePlotSx = {
        [`.${lineClasses.line}`]: {
            strokeWidth: 1
        },
        [`[data-series$='dash']`]: { strokeLinecap: "round", strokeDasharray: "4, 4" }
    };
    const areaPlotSx = {
        [`.${lineClasses.area}`]: { fillOpacity: CHART_AREA_FILL_OPACITY }
    };
    const chartSx = {
        [`.${chartsAxisHighlightClasses.root}`]: { stroke: divider, strokeDasharray: "none" },
        [`.${chartsGridClasses.line}`]: {
            stroke: textSecondary,
            strokeOpacity: 0.3,
            strokeWidth: 2.5,
            strokeLinecap: "round",
            strokeDasharray: "0, 12"
        },
        [`.${axisClasses.root}`]: {
            [` .${axisClasses.line}`]: { stroke: divider },
            [`.${axisClasses.tickLabel}`]: { fill: textSecondary, fontSize: FONT_SIZES.caption }
        },
        [`.${referenceLineClasses.line}`]: {
            strokeWidth: 1,
            stroke: `${divider} !important`
        }
    };

    return { linePlotSx, areaPlotSx, chartSx };
};

function getSeriesConfig<T extends { date: number }>({
    seriesConfig,
    axisFormat,
    index
}: {
    seriesConfig: SeriesConfig<T>;
    axisFormat: FormatType;
    index: number;
}) {
    const { dataKey, dash, fill, deltaFill, color, valueFormatter, hideLine } = seriesConfig;
    return {
        ...seriesConfig,
        yAxisId: deltaFill ? "hidden" : "y-axis",
        type: "line",
        curve: "bumpX",
        showMark: false,
        id: `${dataKey}-${index}-${dash ? "dash" : "solid"}`,
        area: fill ? true : false,
        // Anchor the fill to the bottom of the visible Y-domain, not data-value 0,
        // so it doesn't overflow past the plot and cover the X-axis labels when the
        // Y-axis doesn't start at zero.
        ...(fill ? { baseline: "min" as const } : {}),
        color: !deltaFill && !hideLine ? color : "transparent",
        valueFormatter: valueFormatter ?? SERIES_FORMATTERS[axisFormat],
        label: getConditionalLabel(seriesConfig.label, seriesConfig.labelCondensed),
        labelMarkType: getLabelMark({ dash, fill, deltaFill, hideLine })
    } as const;
}

function getXAxisOverride(xAxis: XAxisOverride | undefined, timeTickValues: number[] | undefined) {
    const base = { id: "x-axis", dataKey: "date", domainLimit: "strict" as const };

    if (xAxis?.scaleOverride === "band") {
        return {
            ...base,
            scaleType: "band" as const,
            ...(xAxis?.groups ? { groups: xAxis.groups } : {}),
            ...(xAxis?.valueFormatter ? { valueFormatter: xAxis.valueFormatter } : {})
        };
    }

    return {
        ...base,
        scaleType: "time" as const,
        valueFormatter: getTimeAxisFormatter(timeTickValues),
        ...(timeTickValues ? { tickInterval: timeTickValues } : {})
    };
}

// Show time-of-day in tick labels only when ticks are spaced less than a day apart;
// otherwise the date alone is unambiguous.
function getTimeAxisFormatter(tickValues: number[] | undefined) {
    const subDay = !!tickValues && tickValues.length >= 2 && tickValues[1] - tickValues[0] < TIME.DAY;
    return AXIS_FORMATTERS[subDay ? FormatType.Datetime : FormatType.Date];
}

function getTimeAxisStepSize(domainSeconds: number): number {
    const tickCount = (stride: number) => domainSeconds / stride;
    const inWindow = TIME_TICK_STEPS.filter((s) => tickCount(s) >= MIN_TIME_TICKS && tickCount(s) <= MAX_TIME_TICKS);
    if (inWindow.length) return inWindow[inWindow.length - 1];
    return TIME_TICK_STEPS.find((s) => tickCount(s) <= MAX_TIME_TICKS) ?? TIME_TICK_STEPS[TIME_TICK_STEPS.length - 1];
}

// Build the array of tick timestamps (in seconds) for the visible [min, max] domain.
function getTimeAxisTicks(minSec: number, maxSec: number): number[] {
    const step = getTimeAxisStepSize(maxSec - minSec);
    const anchor = step < TIME.DAY ? timeHour : timeDay;
    const startOffset =
        step < TIME.DAY ? TIME.HOUR : step < TIME.WEEK ? TIME.DAY : step < TIME.MONTH ? TIME.WEEK : TIME.MONTH;
    const start = anchor.ceil(new Date((minSec + startOffset) * 1000)).getTime() / 1000;
    const ticks: number[] = [];
    for (let t = start; t <= maxSec; t += step) ticks.push(t);
    return ticks;
}

function getYAxisConfig({
    extrema,
    axisFormat,
    disableConstraint = false
}: {
    extrema: { min: number; max: number } | undefined;
    axisFormat: FormatType;
    disableConstraint?: boolean;
}) {
    return {
        position: "right" as const,
        valueFormatter: AXIS_FORMATTERS[axisFormat],
        id: "y-axis",
        scaleType: "linear" as const,
        width: "auto" as const,
        ...(extrema
            ? getYAxisDomain({ ...extrema, disableConstraint: disableConstraint })
            : { ...DEFAULT_EXTREMA, valueFormatter: AXIS_FORMATTERS[FormatType.Hidden] })
    };
}

function getValidDataset<T extends { date: number }>({ data, series }: { data: T[]; series: SeriesConfig<T>[] }): T[] {
    const keys = series.map((s) => s.dataKey);
    return data
        .filter((d) => keys.some((key) => typeof d[key] === "number" && Number.isFinite(d[key])))
        .sort((a, b) => a.date - b.date);
}

/**
 * Finds the min and max values in a dataset, optionally rounded to "nice" round
 * numbers suitable for chart axis bounds (e.g. 50K, 60K instead of 51.2K, 58.7K).
 *
 * When `xRange` is supplied, y extrema are computed only across points inside that window
 * so the axis fits the visible time selector rather than the full fetched dataset.
 */
function getDatasetExtrema<T extends { date: number }>({
    data,
    series,
    xRange
}: {
    data: T[];
    series: SeriesConfig<T>[];
    xRange?: { min: number; max: number };
}) {
    if (data.length === 0) return { xExtrema: undefined, yExtrema: undefined };
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;
    for (const d of data) {
        if (d.date < xMin) xMin = d.date;
        if (d.date > xMax) xMax = d.date;
        if (xRange && (d.date < xRange.min || d.date > xRange.max)) continue;
        for (const s of series) {
            if (s.hideLine) continue;
            const value = d[s.dataKey];
            // Skip delta-fill series since they represent ranges, not values
            if (!s.deltaFill && typeof value === "number") {
                if (value < yMin) yMin = value;
                if (value > yMax) yMax = value;
            }
        }
    }

    if (xRange && yMin === Infinity) {
        for (const d of data) {
            for (const s of series) {
                if (s.hideLine || s.deltaFill) continue;
                const value = d[s.dataKey];
                if (typeof value === "number") {
                    if (value < yMin) yMin = value;
                    if (value > yMax) yMax = value;
                }
            }
        }
    }

    return { xExtrema: { min: xMin, max: xMax }, yExtrema: { min: yMin, max: yMax } };
}

/**
 * Rounds a [min, max] range outward to clean multiples with a small buffer.
 */
function getYAxisDomain({
    min: extremaMin,
    max: extremaMax,
    targetTickCount = 5,
    bufferMultiplier = 0.1,
    disableConstraint = false
}: {
    min: number;
    max: number;
    targetTickCount?: number;
    bufferMultiplier?: number;
    disableConstraint?: boolean;
}) {
    const min = disableConstraint ? Math.min(extremaMin, 0) : extremaMin;
    // Guard `max === min`: downstream step-size hits log10(0) → NaN axis without a nonzero range.
    const max = extremaMax === min ? min + Math.max(Math.abs(min) * 0.1, 1e-6) : extremaMax;
    const buffer = (max - min) * bufferMultiplier;
    const bufferedMin = min - buffer;
    const bufferedMax = max + buffer;

    // Pick a step size that will produce clean tick labels (e.g. 1K, 2.5K, 5K)
    const step = getYAxisStepSize(bufferedMin, bufferedMax, targetTickCount);

    // Round min down and max up to the nearest multiples of step
    const flooredMin = Math.floor(bufferedMin / step) * step;
    const niceMax = Math.ceil(bufferedMax / step) * step;

    // If the data is all non-negative, don't let the axis show negative (buffer can result in negative value for small min)
    const niceMin = min >= 0 ? Math.max(flooredMin, 0) : flooredMin;

    return { min: niceMin, max: niceMax };
}

/**
 * Picks a "nice" step size for the given range
 */
function getYAxisStepSize(min: number, max: number, targetTickCount: number) {
    const roughStep = (max - min) / targetTickCount;

    // Split the rough step into its order of magnitude (e.g. 1000) with coefficient
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const coefficient = roughStep / magnitude;

    // Round the coefficient up to the next "nice" value so tick labels are clean
    const niceCoefficients = [1, 2, 2.5, 5, 10];
    const niceCoefficient = niceCoefficients.find((c) => coefficient <= c) ?? 10;

    return niceCoefficient * magnitude;
}

export function useProjectionStartTime(pollingInterval = TIME.MINUTE * 5) {
    const [now, setNow] = useState<number>(getUnixTs() + TIME.MINUTE);

    useEffect(() => {
        if (!pollingInterval) return;
        const interval = setInterval(() => {
            setNow(getUnixTs() + TIME.MINUTE);
        }, pollingInterval);

        return () => clearInterval(interval);
    }, [pollingInterval]);

    return { now };
}

export function useTimeWindow({
    storageKey,
    defaultPeriod = TIME.WEEK,
    options = DEFAULT_TIME_DOMAIN_PERIODS
}: {
    storageKey?: string;
    defaultPeriod?: number;
    options?: TimeDomainPeriod[];
} = {}) {
    const [period, setPeriod] = useLocalStorage<number>(storageKey ?? "TimeDomainSelect", defaultPeriod);
    const [now, setNow] = useState(() => getUnixTs());
    useEffect(() => {
        const interval = setInterval(() => setNow(getUnixTs()), TIME.MINUTE * 1000);
        return () => clearInterval(interval);
    }, []);
    const xDomain = useMemo<[number, number]>(() => [now - period, now], [now, period]);
    return { period, setPeriod, xDomain, options };
}
